import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Upload, Trash2, Lock, ImagePlus, LogOut } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { useToast } from "../hooks/use-toast";
import { getGallery, addGalleryItem, deleteGalleryItem, verifyPasscode, assetUrl } from "../lib/api";
import { resizeImage } from "../lib/image";

export default function Admin() {
  const { toast } = useToast();
  const [authed, setAuthed] = useState(false);
  const [passcode, setPasscode] = useState("");
  const [items, setItems] = useState([]);
  const [caption, setCaption] = useState("");
  const [pending, setPending] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => window.scrollTo({ top: 0 }), []);

  const load = () => getGallery().then(setItems).catch(() => {});
  useEffect(() => { if (authed) load(); }, [authed]);

  const login = async (e) => {
    e.preventDefault();
    try {
      const ok = await verifyPasscode(passcode);
      if (ok) { setAuthed(true); toast({ title: "Welcome to Gallery Manager" }); }
      else toast({ title: "Incorrect passcode" });
    } catch {
      toast({ title: "Could not verify. Try again." });
    }
  };

  const onFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast({ title: "Please choose an image." }); return; }
    try {
      const dataUrl = await resizeImage(file, 1600, 0.82);
      setPending(dataUrl);
    } catch {
      toast({ title: "Could not read that image. Try another photo." });
    }
  };

  const upload = async () => {
    if (!pending) { toast({ title: "Choose an image first." }); return; }
    setBusy(true);
    try {
      await addGalleryItem({ image: pending, caption }, passcode);
      setPending(null); setCaption("");
      await load();
      toast({ title: "Photo added to gallery" });
    } catch {
      toast({ title: "Upload failed. Check your passcode." });
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    try {
      await deleteGalleryItem(id, passcode);
      setItems((prev) => prev.filter((i) => i.id !== id));
      toast({ title: "Photo removed" });
    } catch {
      toast({ title: "Could not remove photo." });
    }
  };

  if (!authed) {
    return (
      <div className="bg-[#FBF8F3] min-h-screen">
        <Navbar />
        <div className="pt-32 pb-24 max-w-md mx-auto px-5">
          <Link to="/" className="inline-flex items-center gap-2 text-[#6B6259] hover:text-[#A17E4A] mb-8">
            <ArrowLeft className="w-4 h-4" /> Back to Home
          </Link>
          <div className="bg-white rounded-3xl p-8 shadow-lg">
            <div className="w-14 h-14 rounded-2xl bg-[#A17E4A]/12 flex items-center justify-center mb-5">
              <Lock className="w-6 h-6 text-[#A17E4A]" />
            </div>
            <h1 className="font-serif text-3xl text-[#2A2521] mb-2">Gallery Manager</h1>
            <p className="text-[#6B6259] mb-6">Enter the admin passcode to add or remove gallery photos.</p>
            <form onSubmit={login} className="space-y-4">
              <Input
                type="password"
                value={passcode}
                onChange={(e) => setPasscode(e.target.value)}
                placeholder="Admin passcode"
                className="h-12 bg-[#FBF8F3] border-[#E7E0D6] focus-visible:ring-[#A17E4A]"
              />
              <Button type="submit" className="w-full h-12 bg-[#A17E4A] hover:bg-[#8a6a3c] text-white rounded-full">
                Unlock
              </Button>
            </form>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="bg-[#FBF8F3] min-h-screen">
      <Navbar />
      <div className="pt-28 pb-20 max-w-6xl mx-auto px-5 md:px-8">
        <div className="flex items-center justify-between mb-8">
          <Link to="/" className="inline-flex items-center gap-2 text-[#6B6259] hover:text-[#A17E4A]">
            <ArrowLeft className="w-4 h-4" /> Back to Home
          </Link>
          <button onClick={() => setAuthed(false)} className="inline-flex items-center gap-2 text-[#6B6259] hover:text-[#A17E4A]">
            <LogOut className="w-4 h-4" /> Lock
          </button>
        </div>

        <h1 className="font-serif text-4xl text-[#2A2521] mb-8">Gallery Manager</h1>

        {/* Add new */}
        <div className="bg-white rounded-2xl p-6 shadow-sm mb-10">
          <h2 className="font-serif text-xl text-[#2A2521] mb-4">Add a Photo</h2>
          <div className="grid md:grid-cols-2 gap-6 items-start">
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => onFile(e.target.files[0])} />
            <div
              onClick={() => fileRef.current?.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); onFile(e.dataTransfer.files[0]); }}
              className="border-2 border-dashed border-[#D9CDB8] rounded-xl p-8 text-center cursor-pointer hover:border-[#A17E4A] hover:bg-[#FBF8F3] transition-colors"
            >
              {pending ? (
                <img src={pending} alt="Preview" className="max-h-48 mx-auto rounded-lg" />
              ) : (
                <div className="flex flex-col items-center gap-3 text-[#8A8177]">
                  <ImagePlus className="w-9 h-9 text-[#A17E4A]" />
                  <p>Click or drag a photo here</p>
                </div>
              )}
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-[#4A423A] mb-2">Caption</label>
                <Input
                  value={caption}
                  onChange={(e) => setCaption(e.target.value)}
                  placeholder="e.g. Elegant Living Room Drapes"
                  className="h-12 bg-[#FBF8F3] border-[#E7E0D6] focus-visible:ring-[#A17E4A]"
                />
              </div>
              <Button onClick={upload} disabled={busy} className="w-full h-12 bg-[#A17E4A] hover:bg-[#8a6a3c] text-white rounded-full">
                <Upload className="w-4 h-4 mr-2" /> {busy ? "Uploading…" : "Add to Gallery"}
              </Button>
            </div>
          </div>
        </div>

        {/* Existing */}
        <h2 className="font-serif text-xl text-[#2A2521] mb-4">Current Photos ({items.length})</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.map((it) => (
            <div key={it.id} className="group relative rounded-xl overflow-hidden bg-white shadow-sm">
              <img src={assetUrl(it.image)} alt={it.caption} className="w-full h-52 object-cover" />
              <div className="p-4 flex items-center justify-between gap-3">
                <span className="text-sm text-[#4A423A] truncate">{it.caption || "Untitled"}</span>
                <button
                  onClick={() => remove(it.id)}
                  className="shrink-0 w-9 h-9 rounded-lg bg-[#FBEBEA] text-[#C0392B] flex items-center justify-center hover:bg-[#C0392B] hover:text-white transition-colors"
                  aria-label="Delete"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
      <Footer />
    </div>
  );
}
